import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react';
import {
  createJourneyTimeline,
  createLogEntry,
  createFiredTriggerEntry,
  createNovaNotificationEntry,
  createLossHistoryEntry,
  USER_EXTENSION_DEFAULTS,
} from '../data/models';
import { DEMO_PROFILE_MAYA, DEMO_FRIENDS_MAYA, DEMO_POSTS_MAYA } from '../data';

const AppContext = createContext();

const STORAGE_KEYS = {
  user: 'orivonai:user',
  journey: 'orivonai:journey',
  accounts: 'orivonai:accounts',
  doctorReviews: 'orivonai:doctorReviews',
  friends: 'orivonai:friends',
  dms: 'orivonai:dms',
  socialPosts: 'orivonai:socialPosts',
  joinedGroupsByUser: 'orivonai:joinedGroupsByUser',
  journeyTimelines: 'orivonai:journeyTimelines',
  logs: 'orivonai:logs',
  firedTriggers: 'orivonai:firedTriggers',
  novaNotifications: 'orivonai:novaNotifications',
  /** PRIVACY: lossHistory MUST be encrypted with a separate key. Never in analytics/admin/logs. */
  lossHistory: 'orivonai:lossHistory',
  healthRecord: 'orivonai:healthRecord',
  myDoctors: 'orivonai:myDoctors',
  medicationDoseLog: 'orivonai:medicationDoseLog',
  sideEffectsLog: 'orivonai:sideEffectsLog',
  discoverProfile: 'orivonai:discoverProfile',
  fertilityFinances: 'orivonai:fertilityFinances',
};

async function hashPassword(email, password) {
  const raw = `${String(email).toLowerCase().trim()}::${password}`;
  const subtleCrypto = typeof crypto !== 'undefined' && crypto?.subtle ? crypto.subtle : null;
  if (subtleCrypto) {
    const buf = await subtleCrypto.digest('SHA-256', new TextEncoder().encode(raw));
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  return btoa(unescape(encodeURIComponent(raw)));
}

function safeJsonParse(value, fallback) {
  try {
    if (!value) return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function uid(prefix = 'id') {
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}

export function AppProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [msgCount, setMsgCount] = useState(0);
  const [novaDraft, setNovaDraft] = useState('');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi there 💕 I'm Nova, your OrivonAI companion. I'm here for every part of your journey — the questions, the 2am worries, and the wins. What chapter are you in right now?"
    }
  ]);

  const [journey, setJourney] = useState({
    activeCycleId: null,
    cycles: [],
  });

  // doctorId -> [{ id, doctorId, userName, rating, text, createdAt }]
  const [doctorReviews, setDoctorReviews] = useState({});

  // userEmail -> [{ id, identifier, name, createdAt }]
  const [friendsByUser, setFriendsByUser] = useState({});

  // threadKey -> [{ id, fromIdentifier, toIdentifier, content, createdAt }]
  const [dmThreadsByKey, setDmThreadsByKey] = useState({});
  // [{ id, userName, userEmail, topic, groupId, content, createdAt }]
  const [socialPosts, setSocialPosts] = useState([]);
  // userEmail -> [groupId, ...]
  const [joinedGroupsByUser, setJoinedGroupsByUser] = useState({});

  // Nova proactive intelligence
  const [journeyTimelines, setJourneyTimelines] = useState({});
  const [logs, setLogs] = useState([]);
  const [firedTriggers, setFiredTriggers] = useState([]);
  const [novaNotifications, setNovaNotifications] = useState([]);
  const [novaPresenceBar, setNovaPresenceBar] = useState(null);

  // Health record (per-user, keyed by email)
  const [healthRecordByUser, setHealthRecordByUser] = useState({});
  // My doctors / care team (per-user)
  const [myDoctorsByUser, setMyDoctorsByUser] = useState({});
  // Medication dose tracking & side effects
  const [medicationDoseLogByUser, setMedicationDoseLogByUser] = useState({});
  const [sideEffectsLogByUser, setSideEffectsLogByUser] = useState({});
  const [discoverProfileByUser, setDiscoverProfileByUser] = useState({});
  const [fertilityFinancesByUser, setFertilityFinancesByUser] = useState({});

  useEffect(() => {
    const storedUser = safeJsonParse(localStorage.getItem(STORAGE_KEYS.user), null);
    const storedJourney = safeJsonParse(localStorage.getItem(STORAGE_KEYS.journey), null);
    const storedDoctorReviews = safeJsonParse(localStorage.getItem(STORAGE_KEYS.doctorReviews), {});
    const storedFriends = safeJsonParse(localStorage.getItem(STORAGE_KEYS.friends), {});
    const storedDms = safeJsonParse(localStorage.getItem(STORAGE_KEYS.dms), {});
    const storedSocialPosts = safeJsonParse(localStorage.getItem(STORAGE_KEYS.socialPosts), []);
    const storedJoinedGroupsByUser = safeJsonParse(localStorage.getItem(STORAGE_KEYS.joinedGroupsByUser), {});
    const storedJourneyTimelines = safeJsonParse(localStorage.getItem(STORAGE_KEYS.journeyTimelines), {});
    const storedLogs = safeJsonParse(localStorage.getItem(STORAGE_KEYS.logs), []);
    const storedFiredTriggers = safeJsonParse(localStorage.getItem(STORAGE_KEYS.firedTriggers), []);
    const storedNovaNotifications = safeJsonParse(localStorage.getItem(STORAGE_KEYS.novaNotifications), []);
    const storedHealthRecord = safeJsonParse(localStorage.getItem(STORAGE_KEYS.healthRecord), {});
    const storedMyDoctors = safeJsonParse(localStorage.getItem(STORAGE_KEYS.myDoctors), {});
    const storedDoseLog = safeJsonParse(localStorage.getItem(STORAGE_KEYS.medicationDoseLog), {});
    const storedSideEffects = safeJsonParse(localStorage.getItem(STORAGE_KEYS.sideEffectsLog), {});
    const storedDiscoverProfile = safeJsonParse(localStorage.getItem(STORAGE_KEYS.discoverProfile), {});
    const storedFinances = safeJsonParse(localStorage.getItem(STORAGE_KEYS.fertilityFinances), {});
    if (storedUser) {
      setUser({
        ...USER_EXTENSION_DEFAULTS,
        ...storedUser,
        last_login_date: new Date().toISOString().slice(0, 10),
        email: storedUser.email || '',
        first_name: storedUser.first_name ?? storedUser.name?.split(/\s+/)[0] ?? '',
        last_name: storedUser.last_name ?? '',
        date_of_birth: storedUser.date_of_birth || storedUser.birthday || '',
        zip_code: storedUser.zip_code || '',
        journeyType: storedUser.journeyType || storedUser.journey_type || 'ttc',
        journey_type: storedUser.journeyType || storedUser.journey_type || 'ttc',
        activeChapters: Array.isArray(storedUser.activeChapters) && storedUser.activeChapters.length > 0
          ? storedUser.activeChapters
          : (() => {
              const jt = storedUser.journeyType || storedUser.journey_type || 'ttc';
              const fromInterested = Array.isArray(storedUser.interestedJourneys) ? storedUser.interestedJourneys : [];
              const map = { ivf: 'fertility', 'first-chapter': 'ttc', cycle: 'ttc', menopause: 'postpartum', baby: 'postpartum' };
              const normalized = fromInterested.length > 0
                ? fromInterested.map((j) => map[j] || j).filter(Boolean)
                : [jt === 'ivf' ? 'fertility' : jt];
              return [...new Set(normalized)];
            })(),
        journey_detail: storedUser.journey_detail || '',
        has_loss: Boolean(storedUser.has_loss),
        loss_support: Boolean(storedUser.loss_support),
        surrogacy_path: Boolean(storedUser.surrogacy_path),
        children: Array.isArray(storedUser.children) ? storedUser.children : [],
        proactive_mode: storedUser.proactive_mode || 'in_app',
      });
    }
    if (storedJourney) setJourney(storedJourney);
    if (storedJourneyTimelines && typeof storedJourneyTimelines === 'object') setJourneyTimelines(storedJourneyTimelines);
    if (Array.isArray(storedLogs)) setLogs(storedLogs);
    if (Array.isArray(storedFiredTriggers)) setFiredTriggers(storedFiredTriggers);
    if (Array.isArray(storedNovaNotifications)) setNovaNotifications(storedNovaNotifications);
    if (storedHealthRecord && typeof storedHealthRecord === 'object') setHealthRecordByUser(storedHealthRecord);
    if (storedMyDoctors && typeof storedMyDoctors === 'object') setMyDoctorsByUser(storedMyDoctors);
    if (storedDoseLog && typeof storedDoseLog === 'object') setMedicationDoseLogByUser(storedDoseLog);
    if (storedSideEffects && typeof storedSideEffects === 'object') setSideEffectsLogByUser(storedSideEffects);
    if (storedDiscoverProfile && typeof storedDiscoverProfile === 'object') setDiscoverProfileByUser(storedDiscoverProfile);
    if (storedFinances && typeof storedFinances === 'object') setFertilityFinancesByUser(storedFinances);
    if (storedDoctorReviews) setDoctorReviews(storedDoctorReviews);
    if (storedFriends) setFriendsByUser(storedFriends);
    if (storedDms) setDmThreadsByKey(storedDms);
    if (Array.isArray(storedSocialPosts)) setSocialPosts(storedSocialPosts);
    if (storedJoinedGroupsByUser) setJoinedGroupsByUser(storedJoinedGroupsByUser);
  }, []);

  function saveContext() {
    if (user) {
      const toSave = {
        ...user,
        email: user.email || '',
        first_name: user.first_name ?? user.name?.split(/\s+/)[0] ?? '',
        last_name: user.last_name ?? '',
        date_of_birth: user.date_of_birth || user.birthday || '',
        zip_code: user.zip_code || '',
        journeyType: user.journeyType || user.journey_type || 'ttc',
        journey_type: user.journeyType || user.journey_type || 'ttc',
        activeChapters: Array.isArray(user.activeChapters) && user.activeChapters.length > 0 ? user.activeChapters : undefined,
        journey_detail: user.journey_detail || '',
        has_loss: Boolean(user.has_loss),
        loss_support: Boolean(user.loss_support),
        surrogacy_path: Boolean(user.surrogacy_path),
        children: Array.isArray(user.children) ? user.children : [],
        proactive_mode: user.proactive_mode || 'in_app',
      };
      localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(toSave));
    }
  }

  useEffect(() => {
    saveContext();
  }, [user]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.journey, JSON.stringify(journey));
  }, [journey]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.doctorReviews, JSON.stringify(doctorReviews));
  }, [doctorReviews]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.friends, JSON.stringify(friendsByUser));
  }, [friendsByUser]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.dms, JSON.stringify(dmThreadsByKey));
  }, [dmThreadsByKey]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.socialPosts, JSON.stringify(socialPosts));
  }, [socialPosts]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.joinedGroupsByUser, JSON.stringify(joinedGroupsByUser));
  }, [joinedGroupsByUser]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.journeyTimelines, JSON.stringify(journeyTimelines));
  }, [journeyTimelines]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.logs, JSON.stringify(logs));
  }, [logs]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.firedTriggers, JSON.stringify(firedTriggers));
  }, [firedTriggers]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.novaNotifications, JSON.stringify(novaNotifications));
  }, [novaNotifications]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.healthRecord, JSON.stringify(healthRecordByUser));
  }, [healthRecordByUser]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.myDoctors, JSON.stringify(myDoctorsByUser));
  }, [myDoctorsByUser]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.medicationDoseLog, JSON.stringify(medicationDoseLogByUser));
  }, [medicationDoseLogByUser]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.sideEffectsLog, JSON.stringify(sideEffectsLogByUser));
  }, [sideEffectsLogByUser]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.discoverProfile, JSON.stringify(discoverProfileByUser));
  }, [discoverProfileByUser]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.fertilityFinances, JSON.stringify(fertilityFinancesByUser));
  }, [fertilityFinancesByUser]);

  // Seed Maya demo profile when user's discover profile is empty (first-time demo)
  const seededRef = useRef(false);
  useEffect(() => {
    if (!user?.email || seededRef.current) return;
    const key = String(user.email).toLowerCase().trim();
    const existing = discoverProfileByUser?.[key];
    const isEmpty = !existing || (!existing.bio && !existing.fertilityJourney && !existing.location && !existing.clinic);
    if (!isEmpty) return;
    seededRef.current = true;
    setDiscoverProfileByUser((prev) => ({
      ...prev,
      [key]: { ...DEMO_PROFILE_MAYA },
    }));
    setFriendsByUser((prev) => ({
      ...prev,
      [key]: DEMO_FRIENDS_MAYA,
    }));
    setJoinedGroupsByUser((prev) => ({
      ...prev,
      [key]: ['ivf-warriors', 'low-amh'],
    }));
    setSocialPosts((prev) => {
      const demoPosts = DEMO_POSTS_MAYA.map((p) => ({
        ...p,
        userEmail: key,
        userName: 'Maya',
      }));
      return [...demoPosts, ...prev];
    });
    setUser((prev) => ({
      ...prev,
      name: 'Maya',
      first_name: 'Maya',
      journeyType: 'ivf',
      journey_type: 'ivf',
      date_of_birth: DEMO_PROFILE_MAYA.birthday,
      birthday: DEMO_PROFILE_MAYA.birthday,
      activeChapters: prev?.activeChapters?.length ? prev.activeChapters : ['fertility'],
    }));
  }, [user?.email, discoverProfileByUser]);

  function getFertilityFinances(u) {
    const key = normalizeEmail((u || user)?.email);
    if (!key) return { expenses: [], ivfEstimate: null };
    const byUser = fertilityFinancesByUser?.[key];
    return typeof byUser === 'object' && byUser !== null
      ? { expenses: Array.isArray(byUser.expenses) ? byUser.expenses : [], ivfEstimate: byUser.ivfEstimate || null }
      : { expenses: [], ivfEstimate: null };
  }

  function addExpense(expense) {
    const key = getUserKey();
    if (!key) return { ok: false, error: 'Please sign in.' };
    const entry = {
      id: uid('exp'),
      date: expense.date || new Date().toISOString().slice(0, 10),
      category: expense.category || 'other',
      description: String(expense.description || '').trim(),
      amount: Number(expense.amount) || 0,
      receiptUrl: expense.receiptUrl || null,
    };
    setFertilityFinancesByUser((prev) => {
      const current = prev?.[key] || { expenses: [], ivfEstimate: null };
      return { ...prev, [key]: { ...current, expenses: [entry, ...(current.expenses || [])] } };
    });
    return { ok: true };
  }

  function removeExpense(expenseId) {
    const key = getUserKey();
    if (!key) return;
    setFertilityFinancesByUser((prev) => {
      const current = prev?.[key] || { expenses: [], ivfEstimate: null };
      return { ...prev, [key]: { ...current, expenses: (current.expenses || []).filter((e) => e.id !== expenseId) } };
    });
  }

  function saveIvfEstimate(estimate) {
    const key = getUserKey();
    if (!key) return;
    setFertilityFinancesByUser((prev) => {
      const current = prev?.[key] || { expenses: [], ivfEstimate: null };
      return { ...prev, [key]: { ...current, ivfEstimate: estimate } };
    });
  }

  function getDiscoverProfile(u) {
    const key = normalizeEmail((u || user)?.email);
    if (!key) return {};
    const byUser = discoverProfileByUser?.[key];
    return typeof byUser === 'object' && byUser !== null ? byUser : {};
  }

  function updateDiscoverProfile(patch) {
    const key = getUserKey();
    if (!key) return;
    setDiscoverProfileByUser((prev) => {
      const current = typeof prev?.[key] === 'object' && prev?.[key] !== null ? prev[key] : {};
      return { ...prev, [key]: { ...current, ...patch } };
    });
  }

  function getMedicationDoseLog(medId, u) {
    const key = normalizeEmail((u || user)?.email);
    if (!key || !medId) return {};
    const byUser = medicationDoseLogByUser?.[key];
    const byMed = byUser?.[medId];
    return typeof byMed === 'object' && byMed !== null ? byMed : {};
  }

  function logDoseTaken(medId, date, time) {
    const key = getUserKey();
    if (!key || !medId) return;
    const dateStr = typeof date === 'string' ? date.slice(0, 10) : new Date().toISOString().slice(0, 10);
    const timeStr = time || new Date().toTimeString().slice(0, 5);
    setMedicationDoseLogByUser((prev) => {
      const byUser = prev?.[key] || {};
      const byMed = byUser[medId] || {};
      return { ...prev, [key]: { ...byUser, [medId]: { ...byMed, [dateStr]: timeStr } } };
    });
  }

  function addSideEffect({ medicationId, medicationName, description, severity }) {
    const key = getUserKey();
    if (!key) return { ok: false, error: 'Please sign in.' };
    const entry = {
      id: uid('se'),
      date: new Date().toISOString().slice(0, 10),
      medicationId: medicationId || '',
      medicationName: medicationName || '',
      description: String(description || '').trim(),
      severity: Math.min(5, Math.max(1, Number(severity) || 1)),
    };
    setSideEffectsLogByUser((prev) => {
      const list = prev?.[key] || [];
      return { ...prev, [key]: [entry, ...list] };
    });
    return { ok: true };
  }

  function getSideEffects(u) {
    const key = normalizeEmail((u || user)?.email);
    if (!key) return [];
    const list = sideEffectsLogByUser?.[key];
    return Array.isArray(list) ? list : [];
  }

  function getMyDoctors(u) {
    const key = normalizeEmail((u || user)?.email);
    if (!key) return [];
    const list = myDoctorsByUser?.[key];
    return Array.isArray(list) ? list : [];
  }

  function addDoctorToCareTeam(doctor) {
    const key = getUserKey();
    if (!key) return { ok: false, error: 'Please sign in.' };
    const entry = {
      id: uid('doc'),
      ...doctor,
      source: doctor.doctorId ? 'data' : 'custom',
    };
    const current = getMyDoctors(user);
    if (doctor.doctorId && current.some((d) => d.doctorId === doctor.doctorId)) {
      return { ok: true, alreadyExists: true };
    }
    setMyDoctorsByUser((prev) => ({ ...prev, [key]: [...current, entry] }));
    return { ok: true };
  }

  function removeDoctorFromCareTeam(doctorEntryId) {
    const key = getUserKey();
    if (!key) return;
    setMyDoctorsByUser((prev) => {
      const list = prev?.[key] || [];
      return { ...prev, [key]: list.filter((d) => d.id !== doctorEntryId) };
    });
  }

  function updateMyDoctor(doctorEntryId, patch) {
    const key = getUserKey();
    if (!key) return;
    setMyDoctorsByUser((prev) => {
      const list = prev?.[key] || [];
      return { ...prev, [key]: list.map((d) => (d.id === doctorEntryId ? { ...d, ...patch } : d)) };
    });
  }

  function getHealthRecord(u) {
    const key = normalizeEmail((u || user)?.email);
    if (!key) return {};
    const byUser = healthRecordByUser?.[key];
    return typeof byUser === 'object' && byUser !== null ? byUser : {};
  }

  function updateHealthRecord(patch) {
    const key = getUserKey();
    if (!key) return;
    setHealthRecordByUser((prev) => {
      const current = typeof prev?.[key] === 'object' && prev?.[key] !== null ? prev[key] : {};
      return { ...prev, [key]: { ...current, ...patch } };
    });
  }

  function userToProfile(u) {
    if (!u) return null;
    const { passwordHash: _, ...rest } = u;
    return rest;
  }

  function completeOnboarding(profile) {
    const withDefaults = {
      ...USER_EXTENSION_DEFAULTS,
      ...profile,
      last_login_date: new Date().toISOString().slice(0, 10),
    };
    setUser(withDefaults);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(withDefaults));
    setMessages([{
      role: 'assistant',
      content: `Hi ${withDefaults.name} 💕 I'm Nova. I've set up your journey tracker and matched you with communities based on what you shared. I'm here for every part of your path — what can I help you with today?`
    }]);
  }

  function getAccounts() {
    return safeJsonParse(localStorage.getItem(STORAGE_KEYS.accounts), {});
  }

  function saveAccounts(acc) {
    localStorage.setItem(STORAGE_KEYS.accounts, JSON.stringify(acc));
  }

  async function registerAccount({
    email,
    password,
    name,
    first_name,
    last_name,
    birthday,
    date_of_birth,
    zip_code,
    interestedJourneys,
    journeyInterests,
    journeyType: journeyTypeParam,
    journey_detail,
    has_loss,
    show_loss_screen,
    children: childrenParam,
    proactive_mode,
    loss_support,
    surrogacy_path,
    first_nova_message,
    needs: needsParam,
    plan: planParam,
  }) {
    const key = String(email).toLowerCase().trim();
    const accounts = getAccounts();
    if (accounts[key]) return { ok: false, error: 'An account with this email already exists.' };
    const passwordHash = await hashPassword(key, password);
    const first = interestedJourneys?.[0] || journeyTypeParam;
    const journeyTypeMap = {
      'first-chapter': 'ttc',
      fertility: 'ivf',
      ivf: 'ivf',
      ttc: 'ttc',
      surrogacy: 'surrogacy',
      pregnancy: 'pregnancy',
      postpartum: 'postpartum',
      baby: 'baby',
      cycle: 'ttc',
      menopause: 'postpartum',
      loss: 'loss',
    };
    const journeyType = (first && journeyTypeMap[first]) || journeyTypeParam || 'ttc';
    const displayName = (first_name && last_name)
      ? `${String(first_name).trim()} ${String(last_name).trim()}`.trim()
      : (name || '').trim();
    const profile = {
      email: key,
      passwordHash,
      name: displayName || 'there',
      first_name: first_name?.trim?.(),
      last_name: last_name?.trim?.(),
      birthday: birthday || date_of_birth || '',
      date_of_birth: date_of_birth || birthday || '',
      zip_code: zip_code || '',
      interestedJourneys: interestedJourneys || [journeyTypeParam || first],
      journeyInterests: journeyInterests || {},
      journeyType,
      journey_type: journeyType,
      journey_detail: journey_detail || '',
      has_loss: Boolean(has_loss),
      show_loss_screen: Boolean(show_loss_screen),
      children: Array.isArray(childrenParam) ? childrenParam : [],
      proactive_mode: proactive_mode || 'in_app',
      needs: Array.isArray(needsParam) ? needsParam : [],
      plan: planParam || 'free',
      loss_support: Boolean(loss_support ?? journeyType === 'loss'),
      surrogacy_path: Boolean(surrogacy_path ?? journeyType === 'surrogacy'),
      ...USER_EXTENSION_DEFAULTS,
      last_login_date: new Date().toISOString().slice(0, 10),
    };
    accounts[key] = profile;
    saveAccounts(accounts);
    const session = userToProfile(profile);
    setUser(session);

    const timelineChildren = (childrenParam || [])
      .filter((c) => c?.birth_year)
      .map((c) => ({ name: c?.name || '', dob: `${c.birth_year}-01-01` }));
    if (timelineChildren.length > 0) {
      const next = createJourneyTimeline(key, { userId: key, chapter: journeyType, children: timelineChildren });
      setJourneyTimelines((prev) => ({ ...prev, [key]: next }));
    }

    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(session));
    const novaMsg = (typeof first_nova_message === 'string' && first_nova_message.trim())
      ? first_nova_message
      : `Hi ${session.name} 💕 Welcome to OrivonAI. I've saved your chapters and interests — ask me anything, anytime.`;
    setMessages([{ role: 'assistant', content: novaMsg }]);
    return { ok: true };
  }

  function getDoctorReviews(doctorId) {
    const list = doctorReviews?.[doctorId];
    if (!Array.isArray(list)) return [];
    return list.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  function getDoctorStats(doctor) {
    const baseReviews = Number(doctor?.reviews || 0);
    const baseRating = Number(doctor?.rating || 0);
    const list = getDoctorReviews(doctor?.id);

    const extraCount = list.length;
    if (!extraCount) return { rating: baseRating, reviews: baseReviews };

    const extraSum = list.reduce((acc, r) => acc + (Number(r.rating) || 0), 0);
    const totalCount = baseReviews + extraCount;
    const rating = totalCount > 0 ? (baseRating * baseReviews + extraSum) / totalCount : 0;
    return { rating: Math.round(rating * 10) / 10, reviews: totalCount };
  }

  function addDoctorReview({ doctorId, rating, text, journeyContext, isAnonymous }) {
    const r = Number(rating);
    const cleanText = String(text || '').trim();
    if (!doctorId) return { ok: false, error: 'Missing doctor.' };
    if (!user) return { ok: false, error: 'Please sign in to review.' };
    if (!Number.isFinite(r) || r < 1 || r > 5) return { ok: false, error: 'Rating must be 1–5.' };
    if (cleanText.length < 5) return { ok: false, error: 'Tell us a little more (5+ characters).' };

    const review = {
      id: uid('review'),
      doctorId,
      userName: isAnonymous ? 'Anonymous' : (user?.name || 'Anonymous'),
      rating: r,
      text: cleanText,
      journeyContext: journeyContext || null,
      isAnonymous: Boolean(isAnonymous),
      createdAt: new Date().toISOString(),
    };

    setDoctorReviews(prev => {
      const prevList = Array.isArray(prev?.[doctorId]) ? prev[doctorId] : [];
      return { ...prev, [doctorId]: [...prevList, review] };
    });

    return { ok: true };
  }

  function threadKeyFor(a, b) {
    const x = String(a || '').toLowerCase().trim();
    const y = String(b || '').toLowerCase().trim();
    const sorted = [x, y].sort();
    return `dm_${sorted[0] || 'x'}_${sorted[1] || 'y'}`;
  }

  function getFriendsForUser(u) {
    const key = u?.email ? String(u.email).toLowerCase().trim() : '';
    if (!key) return [];
    const list = friendsByUser?.[key];
    return Array.isArray(list) ? list : [];
  }

  function addFriend({ identifier, name }) {
    const friendIdentifier = String(identifier || '').toLowerCase().trim();
    const friendName = String(name || '').trim();
    if (!user) return { ok: false, error: 'Please sign in to add friends.' };
    if (!friendIdentifier) return { ok: false, error: 'Enter a friend email/handle.' };
    if (friendIdentifier === String(user.email || '').toLowerCase().trim()) {
      return { ok: false, error: 'You can’t add yourself.' };
    }

    const myKey = String(user.email).toLowerCase().trim();
    const existing = getFriendsForUser(user).find((f) => f.identifier === friendIdentifier);
    if (existing) return { ok: true, alreadyExists: true };

    const friend = {
      id: uid('friend'),
      identifier: friendIdentifier,
      name: friendName || friendIdentifier,
      createdAt: new Date().toISOString(),
    };

    setFriendsByUser((prev) => {
      const prevList = Array.isArray(prev?.[myKey]) ? prev[myKey] : [];
      return { ...prev, [myKey]: [friend, ...prevList] };
    });

    return { ok: true };
  }

  function getDmThread(friendIdentifier) {
    if (!user) return [];
    const key = threadKeyFor(user.email, friendIdentifier);
    const list = dmThreadsByKey?.[key];
    return Array.isArray(list) ? list : [];
  }

  function sendDM({ friendIdentifier, content }) {
    if (!user) return { ok: false, error: 'Please sign in to message.' };
    const friendId = String(friendIdentifier || '').toLowerCase().trim();
    const clean = String(content || '').trim();
    if (!friendId) return { ok: false, error: 'Choose a friend to message.' };
    if (!clean || clean.length < 1) return { ok: false, error: 'Type a message.' };

    const key = threadKeyFor(user.email, friendId);
    const msg = {
      id: uid('dm'),
      fromIdentifier: String(user.email).toLowerCase().trim(),
      toIdentifier: friendId,
      content: clean,
      createdAt: new Date().toISOString(),
    };

    setDmThreadsByKey((prev) => {
      const prevList = Array.isArray(prev?.[key]) ? prev[key] : [];
      return { ...prev, [key]: [...prevList, msg] };
    });

    return { ok: true };
  }

  function normalizeEmail(email) {
    return String(email || '').toLowerCase().trim();
  }

  function getJoinedGroupIds(u) {
    const key = normalizeEmail((u || user)?.email);
    if (!key) return [];
    const list = joinedGroupsByUser?.[key];
    if (!Array.isArray(list)) return [];
    return Array.from(new Set(list));
  }

  function joinGroup(groupId) {
    const cleanGroupId = String(groupId || '').trim();
    if (!user) return { ok: false, error: 'Please sign in to join groups.' };
    if (!cleanGroupId) return { ok: false, error: 'Missing group id.' };

    const key = normalizeEmail(user.email);
    const current = getJoinedGroupIds(user);
    if (current.includes(cleanGroupId)) return { ok: true, alreadyJoined: true };

    setJoinedGroupsByUser((prev) => ({
      ...prev,
      [key]: [...current, cleanGroupId],
    }));
    return { ok: true };
  }

  function leaveGroup(groupId) {
    const cleanGroupId = String(groupId || '').trim();
    if (!user) return { ok: false, error: 'Please sign in to update groups.' };
    if (!cleanGroupId) return { ok: false, error: 'Missing group id.' };

    const key = normalizeEmail(user.email);
    const current = getJoinedGroupIds(user);
    setJoinedGroupsByUser((prev) => ({
      ...prev,
      [key]: current.filter((id) => id !== cleanGroupId),
    }));
    return { ok: true };
  }

  function createSocialPost({ topic, groupId, content }) {
    const cleanTopic = String(topic || '').trim();
    const cleanGroupId = String(groupId || '').trim();
    const cleanContent = String(content || '').trim();

    if (!user) return { ok: false, error: 'Please sign in to post.' };
    if (!cleanTopic) return { ok: false, error: 'Please choose a topic.' };
    if (cleanContent.length < 3) return { ok: false, error: 'Post is too short.' };

    const post = {
      id: uid('post'),
      userName: String(user?.name || 'Anonymous').trim() || 'Anonymous',
      userEmail: normalizeEmail(user?.email),
      topic: cleanTopic,
      groupId: cleanGroupId || null,
      content: cleanContent,
      createdAt: new Date().toISOString(),
    };

    setSocialPosts((prev) => [...prev, post]);
    return { ok: true, post };
  }

  function getSocialPosts({ groupId } = {}) {
    const cleanGroupId = String(groupId || '').trim();
    const list = Array.isArray(socialPosts) ? socialPosts : [];
    return list
      .filter((p) => (!cleanGroupId ? true : String(p?.groupId || '') === cleanGroupId))
      .slice()
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  async function signInWithPassword(email, password) {
    const key = String(email).toLowerCase().trim();
    const accounts = getAccounts();
    const acc = accounts[key];
    if (!acc) return { ok: false, error: 'No account found for that email.' };
    const h = await hashPassword(key, password);
    if (h !== acc.passwordHash) return { ok: false, error: 'Incorrect password.' };
    const session = {
      ...userToProfile(acc),
      ...USER_EXTENSION_DEFAULTS,
      subsequent_pregnancy_after_loss: acc.subsequent_pregnancy_after_loss ?? false,
      recurrent_loss_count: acc.recurrent_loss_count ?? 0,
      last_login_date: new Date().toISOString().slice(0, 10),
    };
    setUser(session);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(session));
    setMessages([{
      role: 'assistant',
      content: `Hi ${session.name} 💕 Good to see you again. Pick up where you left off — what’s on your mind?`,
    }]);
    return { ok: true };
  }

  function signOut() {
    setUser(null);
    localStorage.removeItem(STORAGE_KEYS.user);
    setMessages([{
      role: 'assistant',
      content: "Hi there 💕 I'm Nova. I'll be here when your journey continues in the app.",
    }]);
  }

  function updateAccountProfile(patch) {
    const key = getUserKey();
    if (!key || !user) return;
    const updates = { ...patch };
    if (updates.journeyType != null) updates.journey_type = updates.journeyType;
    setUser((prev) => ({ ...prev, ...updates }));
    const accounts = getAccounts();
    const acc = accounts[key];
    if (acc) {
      const { passwordHash } = acc;
      accounts[key] = { ...acc, ...updates, passwordHash };
      saveAccounts(accounts);
    }
  }

  async function changePassword(currentPassword, newPassword) {
    const key = getUserKey();
    if (!key) return { ok: false, error: 'Please sign in.' };
    const accounts = getAccounts();
    const acc = accounts[key];
    if (!acc) return { ok: false, error: 'Account not found.' };
    const h = await hashPassword(key, currentPassword);
    if (h !== acc.passwordHash) return { ok: false, error: 'Current password is incorrect.' };
    const newHash = await hashPassword(key, newPassword);
    accounts[key] = { ...acc, passwordHash: newHash };
    saveAccounts(accounts);
    return { ok: true };
  }

  async function changeEmail(newEmail, password) {
    const oldKey = getUserKey();
    if (!oldKey) return { ok: false, error: 'Please sign in.' };
    const newKey = String(newEmail).toLowerCase().trim();
    if (newKey === oldKey) return { ok: false, error: 'New email is the same as current.' };
    const accounts = getAccounts();
    const acc = accounts[oldKey];
    if (!acc) return { ok: false, error: 'Account not found.' };
    const h = await hashPassword(oldKey, password);
    if (h !== acc.passwordHash) return { ok: false, error: 'Password is incorrect.' };
    if (accounts[newKey]) return { ok: false, error: 'An account with that email already exists.' };
    const newHash = await hashPassword(newKey, password);
    const profile = { ...acc, email: newKey, passwordHash: newHash };
    delete accounts[oldKey];
    accounts[newKey] = profile;
    saveAccounts(accounts);
    setUser((prev) => ({ ...prev, ...userToProfile(profile) }));
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(userToProfile(profile)));
    setDiscoverProfileByUser((prev) => {
      const next = { ...prev };
      if (prev[oldKey] != null) {
        next[newKey] = prev[oldKey];
        delete next[oldKey];
      }
      return next;
    });
    setFertilityFinancesByUser((prev) => {
      const next = { ...prev };
      if (prev[oldKey] != null) {
        next[newKey] = prev[oldKey];
        delete next[oldKey];
      }
      return next;
    });
    setHealthRecordByUser((prev) => {
      const next = { ...prev };
      if (prev[oldKey] != null) {
        next[newKey] = prev[oldKey];
        delete next[oldKey];
      }
      return next;
    });
    setJourneyTimelines((prev) => {
      const next = { ...prev };
      if (prev[oldKey] != null) {
        next[newKey] = prev[oldKey];
        delete next[oldKey];
      }
      return next;
    });
    setJoinedGroupsByUser((prev) => {
      const next = { ...prev };
      if (prev[oldKey] != null) {
        next[newKey] = prev[oldKey];
        delete next[oldKey];
      }
      return next;
    });
    return { ok: true };
  }

  function addMessage(msg) {
    setMessages(prev => [...prev, msg]);
    if (msg.role === 'user') setMsgCount(prev => prev + 1);
  }

  function ensureActiveCycle() {
    setJourney(prev => {
      if (prev.activeCycleId && prev.cycles.some(c => c.id === prev.activeCycleId)) return prev;
      const first = prev.cycles[0];
      if (first) return { ...prev, activeCycleId: first.id };
      // Seed Cycle 1 (past, with example meds) and Cycle 2 (current active)
      const c1Id = uid('cycle');
      const c2Id = uid('cycle');
      const m1 = { id: uid('med'), name: 'Gonal-F', dose: '225', units: 'IU', schedule: 'Nightly', startDay: '1', endDay: '10', reminderTime: '20:00' };
      const m2 = { id: uid('med'), name: 'Menopur', dose: '75', units: 'IU', schedule: 'Nightly', startDay: '1', endDay: '10', reminderTime: '20:00' };
      const cycle1 = {
        id: c1Id,
        name: 'Cycle 1',
        startedAt: (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); })(),
        stage: user?.journeyType || 'ivf',
        treatmentStepDates: {},
        medications: [m1, m2],
        reminders: [
          { id: uid('rem'), title: 'Gonal-F', time: '20:00', linkedMedicationId: m1.id },
          { id: uid('rem'), title: 'Menopur', time: '20:00', linkedMedicationId: m2.id },
        ],
        notes: '',
      };
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 2);
      const retrievalDate = new Date(startDate);
      retrievalDate.setDate(retrievalDate.getDate() + 10);
      const m3 = { id: uid('med'), name: 'Gonal-F', dose: '300', units: 'IU', schedule: 'Nightly 8pm', startDay: '1', endDay: '10', reminderTime: '20:00' };
      const cycle2 = {
        id: c2Id,
        name: 'Cycle 2',
        startedAt: startDate.toISOString().slice(0, 10),
        stage: user?.journeyType || 'ivf',
        treatmentStepDates: {},
        protocol: 'Antagonist',
        clinic: 'Ohio Fertility Network',
        estimatedRetrieval: retrievalDate.toISOString().slice(0, 10),
        follicleCount: 12,
        medications: [m3],
        reminders: [{ id: uid('rem'), title: 'Gonal-F', time: '20:00', linkedMedicationId: m3.id }],
        notes: '',
      };
      return { activeCycleId: c2Id, cycles: [cycle2, cycle1] };
    });
  }

  function setActiveCycleId(cycleId) {
    setJourney(prev => ({ ...prev, activeCycleId: cycleId }));
  }

  function addCycle(attrs) {
    const { id: providedId, name, startedAt, stage, protocol, clinic, retrievalDate, estimatedRetrieval, follicleCount, ...rest } = attrs || {};
    const cycle = {
      id: providedId || uid('cycle'),
      name: name || `Cycle ${journey.cycles.length + 1}`,
      startedAt: startedAt || new Date().toISOString().slice(0, 10),
      stage: stage || user?.journeyType || 'ivf',
      treatmentStepDates: {},
      medications: [],
      reminders: [],
      notes: '',
      ...(protocol && { protocol }),
      ...(clinic && { clinic }),
      ...(retrievalDate && { retrievalDate, estimatedRetrieval: estimatedRetrieval || retrievalDate }),
      ...(follicleCount != null && { follicleCount }),
      ...rest,
    };
    setJourney(prev => ({
      ...prev,
      activeCycleId: cycle.id,
      cycles: [cycle, ...prev.cycles],
    }));
  }

  function updateCycle(cycleId, patch) {
    setJourney(prev => ({
      ...prev,
      cycles: prev.cycles.map(c => (c.id === cycleId ? { ...c, ...patch } : c)),
    }));
  }

  function addMedication(cycleId, med) {
    const medication = {
      id: uid('med'),
      name: med?.name || '',
      dose: med?.dose || '',
      units: med?.units || '',
      schedule: med?.schedule || med?.frequency || '',
      frequency: med?.frequency || med?.schedule || '',
      route: med?.route || '',
      purpose: med?.purpose || '',
      startDay: med?.startDay || '',
      endDay: med?.endDay || '',
      startDate: med?.startDate || '',
      endDate: med?.endDate || '',
      reminderTime: med?.reminderTime || '',
      notes: med?.notes || '',
    };
    const reminderTime = med?.reminderTime || '';
    const linkedReminder = reminderTime ? {
      id: uid('rem'),
      title: medication.name,
      time: reminderTime,
      linkedMedicationId: medication.id,
      notes: '',
    } : null;
    setJourney(prev => ({
      ...prev,
      cycles: prev.cycles.map(c => {
        if (c.id !== cycleId) return c;
        const reminders = linkedReminder ? [linkedReminder, ...(c.reminders || [])] : (c.reminders || []);
        return { ...c, medications: [medication, ...(c.medications || [])], reminders };
      }),
    }));
  }

  function removeMedication(cycleId, medId) {
    setJourney(prev => ({
      ...prev,
      cycles: prev.cycles.map(c => {
        if (c.id !== cycleId) return c;
        const medications = (c.medications || []).filter(m => m.id !== medId);
        const reminders = (c.reminders || []).filter(r => r.linkedMedicationId !== medId);
        return { ...c, medications, reminders };
      }),
    }));
  }

  function updateMedication(cycleId, medId, patch) {
    setJourney(prev => ({
      ...prev,
      cycles: prev.cycles.map(c =>
        c.id === cycleId
          ? { ...c, medications: (c.medications || []).map(m => (m.id === medId ? { ...m, ...patch } : m)) }
          : c
      ),
    }));
  }

  function addReminder(cycleId, reminder) {
    const r = {
      id: uid('rem'),
      title: reminder?.title || '',
      time: reminder?.time || '09:00',
      linkedMedicationId: reminder?.linkedMedicationId || null,
      notes: reminder?.notes || '',
    };
    setJourney(prev => ({
      ...prev,
      cycles: prev.cycles.map(c =>
        c.id === cycleId ? { ...c, reminders: [r, ...(c.reminders || [])] } : c
      ),
    }));
  }

  function removeReminder(cycleId, reminderId) {
    setJourney(prev => ({
      ...prev,
      cycles: prev.cycles.map(c =>
        c.id === cycleId ? { ...c, reminders: (c.reminders || []).filter(r => r.id !== reminderId) } : c
      ),
    }));
  }

  function getUserKey() {
    return normalizeEmail(user?.email) || '';
  }

  function getJourneyTimeline(u) {
    const key = normalizeEmail((u || user)?.email);
    if (!key) return null;
    const timelines = journeyTimelines?.[key];
    return timelines || null;
  }

  function setJourneyTimeline(timeline) {
    const key = getUserKey();
    if (!key) return;
    const next = createJourneyTimeline(key, { ...timeline, userId: key });
    setJourneyTimelines((prev) => ({ ...prev, [key]: next }));
  }

  function updateJourneyTimeline(patch) {
    const key = getUserKey();
    if (!key) return;
    const current = getJourneyTimeline(user) || createJourneyTimeline(key, { chapter: user?.journeyType || user?.journey_type || 'ttc' });
    const merged = {
      ...current,
      ...patch,
      userId: key,
      ivf_protocol: { ...(current.ivf_protocol || {}), ...(patch.ivf_protocol || {}) },
      cycle_data: { ...(current.cycle_data || {}), ...(patch.cycle_data || {}) },
      key_dates: { ...(current.key_dates || {}), ...(patch.key_dates || {}) },
      updated_at: new Date().toISOString(),
    };
    setJourneyTimelines((prev) => ({ ...prev, [key]: createJourneyTimeline(key, merged) }));
  }

  function addLog(logType, data) {
    const key = getUserKey();
    if (!key) return { ok: false, error: 'Account required to log.' };
    const entry = createLogEntry(key, logType, data);
    setLogs((prev) => [...prev, entry]);
    return { ok: true, log: entry };
  }

  function getLogs({ logType, limit = 50 } = {}) {
    const key = getUserKey();
    if (!key) return [];
    const list = (logs || []).filter((l) => String(l.user_id || '').toLowerCase() === key);
    const filtered = logType ? list.filter((l) => l.log_type === logType) : list;
    return filtered
      .slice()
      .sort((a, b) => new Date(b.logged_at) - new Date(a.logged_at))
      .slice(0, limit);
  }

  function addFiredTrigger(messageId) {
    const key = getUserKey();
    if (!key) return;
    const entry = createFiredTriggerEntry(key, messageId);
    setFiredTriggers((prev) => [...prev, entry]);
  }

  function addNovaNotification({ message_id, message }) {
    const key = getUserKey();
    if (!key) return;
    const entry = createNovaNotificationEntry(key, message_id, message);
    setNovaNotifications((prev) => [...prev, entry]);
  }

  function markNovaNotificationRead(notificationId) {
    setNovaNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
    );
  }

  function showNovaPresenceBar({ theme, message, actionLabel, onAction, dismissStorageKey, contextPill }) {
    setNovaPresenceBar({
      theme,
      message,
      actionLabel,
      contextPill: contextPill || 'Guidance',
      onAction: onAction || (() => {}),
      dismissStorageKey: dismissStorageKey || null,
    });
  }

  function dismissNovaPresenceBar() {
    setNovaPresenceBar(null);
  }

  function getNovaNotificationsForUser(u) {
    const key = normalizeEmail((u || user)?.email);
    if (!key) return [];
    return (novaNotifications || [])
      .filter((n) => String(n.user_id || '').toLowerCase() === key)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }

  function getLossHistory(u) {
    const key = normalizeEmail((u || user)?.email);
    if (!key) return [];
    const raw = localStorage.getItem(STORAGE_KEYS.lossHistory);
    if (!raw) return [];
    try {
      const map = JSON.parse(raw);
      const list = map?.[key];
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  function setLossHistory(entries) {
    const key = getUserKey();
    if (!key) return;
    const raw = localStorage.getItem(STORAGE_KEYS.lossHistory);
    let map = {};
    try {
      if (raw) map = JSON.parse(raw);
    } catch {}
    const validated = entries.map((e) => createLossHistoryEntry(e));
    map[key] = validated;
    localStorage.setItem(STORAGE_KEYS.lossHistory, JSON.stringify(map));
  }

  const friends = useMemo(() => getFriendsForUser(user), [user, friendsByUser]);

  const plan = user?.plan || 'free';
  const freeLimit = 20;
  const messagesRemaining = Math.max(0, freeLimit - msgCount);

  const activeCycle = useMemo(() => {
    if (!journey?.activeCycleId) return null;
    return (journey.cycles || []).find(c => c.id === journey.activeCycleId) || null;
  }, [journey]);

  function updateUserContext(patch) {
    if (!user) return;
    setUser((prev) => ({ ...prev, ...patch }));
  }

  function updateUserChapters(chapters, primaryChapter) {
    if (!user) return;
    const arr = Array.isArray(chapters) ? [...chapters] : [chapters];
    const unique = [...new Set(arr.filter(Boolean))];
    const primary = primaryChapter || unique[0] || user.journeyType;
    const journeyTypeMap = { fertility: 'ivf', cycle: 'ttc', ttc: 'ttc', pregnancy: 'pregnancy', postpartum: 'postpartum', menopause: 'postpartum' };
    const journeyType = journeyTypeMap[primary] || primary;
    setUser((prev) => ({
      ...prev,
      activeChapters: unique.length > 0 ? unique : (prev.activeChapters || [prev.journeyType === 'ivf' ? 'fertility' : prev.journeyType]),
      journeyType,
      journey_type: journeyType,
    }));
  }

  return (
    <AppContext.Provider     value={{
      user, setUser, updateUserContext, updateUserChapters, saveContext,
      messages, addMessage,
      msgCount, messagesRemaining,
      plan, freeLimit,
      completeOnboarding,
      registerAccount,
      signInWithPassword,
      signOut,
      updateAccountProfile,
      changePassword,
      changeEmail,
      novaDraft,
      setNovaDraft,
      journey,
      activeCycle,
      ensureActiveCycle,
      setActiveCycleId,
      addCycle,
      updateCycle,
      addMedication,
      removeMedication,
      updateMedication,
      addReminder,
      getMedicationDoseLog,
      logDoseTaken,
      addSideEffect,
      getSideEffects,
      removeReminder,
      doctorReviews,
      getDoctorReviews,
      getDoctorStats,
      addDoctorReview,
      friends,
      addFriend,
      getDmThread,
      sendDM,
      socialPosts,
      joinedGroupsByUser,
      getJoinedGroupIds,
      joinGroup,
      leaveGroup,
      createSocialPost,
      getSocialPosts,
      journeyTimelines,
      getJourneyTimeline,
      setJourneyTimeline,
      updateJourneyTimeline,
      addLog,
      getLogs,
      addFiredTrigger,
      firedTriggers,
      addNovaNotification,
      novaNotifications,
      getNovaNotificationsForUser,
      markNovaNotificationRead,
      novaPresenceBar,
      showNovaPresenceBar,
      dismissNovaPresenceBar,
      getLossHistory,
      setLossHistory,
      getHealthRecord,
      updateHealthRecord,
      getMyDoctors,
      addDoctorToCareTeam,
      removeDoctorFromCareTeam,
      updateMyDoctor,
      getDiscoverProfile,
      updateDiscoverProfile,
      getFertilityFinances,
      addExpense,
      removeExpense,
      saveIvfEstimate,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
